import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const TeamRoster = ({ team, onUpdate, userRole }) => {
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    role: '',
    contact: '',
    inviteMethod: 'email'
  });

  const roleOptions = [
    { value: 'hacker', label: 'Hacker' },
    { value: 'plumber', label: 'Plumber' },
    { value: 'aircon', label: 'Aircon' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'cleaner', label: 'Cleaner' },
    { value: 'false_ceiling', label: 'False Ceiling' },
    { value: 'tiling', label: 'Tiling' },
    { value: 'carpenter', label: 'Carpenter' },
    { value: 'painter', label: 'Painter' },
    { value: 'main_con', label: 'Main Con' }
  ];

  const inviteMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'username', label: 'Username' }
  ];

  const handleAddMember = () => {
    if (newMember?.name && newMember?.role && newMember?.contact) {
      const updatedTeam = [...team, {
        id: Date.now(),
        ...newMember,
        status: 'pending',
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${newMember?.name}`,
        avatarAlt: `Avatar for ${newMember?.name} - ${newMember?.role} team member`
      }];
      onUpdate(updatedTeam);
      setNewMember({ name: '', role: '', contact: '', inviteMethod: 'email' });
      setIsAddingMember(false);
    }
  };

  const handleRemoveMember = (memberId) => {
    const updatedTeam = team?.filter(member => member?.id !== memberId);
    onUpdate(updatedTeam);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      hacker: 'bg-red-100 text-red-700',
      plumber: 'bg-blue-100 text-blue-700',
      aircon: 'bg-cyan-100 text-cyan-700',
      electrical: 'bg-yellow-100 text-yellow-700',
      cleaner: 'bg-green-100 text-green-700',
      false_ceiling: 'bg-purple-100 text-purple-700',
      tiling: 'bg-orange-100 text-orange-700',
      carpenter: 'bg-amber-100 text-amber-700',
      painter: 'bg-pink-100 text-pink-700',
      main_con: 'bg-indigo-100 text-indigo-700'
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  const isBoss = userRole === 'boss';

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Users" size={20} strokeWidth={2} />
          Team Roster
          <span className="text-sm font-normal text-muted-foreground">({team?.length} members)</span>
        </h2>
        {isBoss && (
          <Button
            variant="outline"
            size="sm"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setIsAddingMember(!isAddingMember)}
          >
            Add Member
          </Button>
        )}
      </div>
      {isAddingMember && isBoss && (
        <div className="bg-muted/50 border border-border rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Add New Team Member</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label="Name"
              type="text"
              placeholder="Enter member name"
              value={newMember?.name}
              onChange={(e) => setNewMember({ ...newMember, name: e?.target?.value })}
              required
            />
            <Select
              label="Role"
              options={roleOptions}
              value={newMember?.role}
              onChange={(value) => setNewMember({ ...newMember, role: value })}
              placeholder="Select role"
              required
            />
            <Select
              label="Invite Method"
              options={inviteMethodOptions}
              value={newMember?.inviteMethod}
              onChange={(value) => setNewMember({ ...newMember, inviteMethod: value })}
            />
            <Input
              label={newMember?.inviteMethod === 'email' ? 'Email' : newMember?.inviteMethod === 'phone' ? 'Phone' : 'Username'}
              type={newMember?.inviteMethod === 'email' ? 'email' : newMember?.inviteMethod === 'phone' ? 'tel' : 'text'}
              placeholder={`Enter ${newMember?.inviteMethod}`}
              value={newMember?.contact}
              onChange={(e) => setNewMember({ ...newMember, contact: e?.target?.value })}
              required
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              iconName="Check"
              onClick={handleAddMember}
              disabled={!newMember?.name || !newMember?.role || !newMember?.contact}
            >
              Add Member
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => {
                setIsAddingMember(false);
                setNewMember({ name: '', role: '', contact: '', inviteMethod: 'email' });
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {team?.map((member) => (
          <div key={member?.id} className="bg-background border border-border rounded-lg p-4 hover:shadow-soft transition-smooth">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                  <Image
                    src={member?.avatar}
                    alt={member?.avatarAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{member?.name}</h3>
                  <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 ${getRoleBadgeColor(member?.role)}`}>
                    {roleOptions?.find(r => r?.value === member?.role)?.label || member?.role}
                  </span>
                </div>
              </div>
              {isBoss && (
                <button
                  onClick={() => handleRemoveMember(member?.id)}
                  className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-error hover:bg-error/10 transition-smooth"
                  aria-label="Remove member"
                >
                  <Icon name="X" size={14} strokeWidth={2} />
                </button>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="Mail" size={14} strokeWidth={2} />
                <span className="truncate">{member?.contact}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  member?.status === 'active' ? 'bg-success' : 
                  member?.status === 'pending' ? 'bg-warning' : 'bg-muted-foreground'
                }`} />
                <span className="text-xs text-muted-foreground capitalize">{member?.status}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamRoster;